import React, { useEffect, useMemo, useRef } from "react";
import { Stack } from "react-bootstrap";
import { colors } from "src/styles";
import styled from "styled-components";
import * as d3 from "d3";

interface WidgetProps {
  caption: string;
}

interface MonthlySpend {
  month: string;
  amount: number;
}

const SpendPerMonthChart = ({ caption }: WidgetProps) => {
  const svgRef = useRef<SVGSVGElement>(null);

  // Static data - to be replaced with API data later
  const monthlySpendData: MonthlySpend[] = useMemo(
    () => [
      { month: "Jan", amount: 45000 },
      { month: "Feb", amount: 52000 },
      { month: "Mar", amount: 48000 },
      { month: "Apr", amount: 55000 },
      { month: "May", amount: 51000 },
      { month: "Jun", amount: 58000 },
      { month: "Jul", amount: 62000 },
      { month: "Aug", amount: 59000 },
      { month: "Sep", amount: 54000 },
      { month: "Oct", amount: 57000 },
      { month: "Nov", amount: 61000 },
      { month: "Dec", amount: 65000 },
    ],
    [],
  );

  useEffect(() => {
    if (!svgRef.current || monthlySpendData.length === 0) return;

    const margin = { top: 20, right: 30, bottom: 40, left: 70 };
    const width = 500 - margin.left - margin.right;
    const height = 250 - margin.top - margin.bottom;

    // Clear previous content
    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // X scale
    const x = d3
      .scaleBand()
      .domain(monthlySpendData.map((d) => d.month))
      .range([0, width])
      .padding(0.3);

    // Y scale
    const maxAmount = d3.max(monthlySpendData, (d) => d.amount) || 0;
    const y = d3
      .scaleLinear()
      .domain([0, maxAmount * 1.1])
      .range([height, 0]);

    // Add X axis
    svg
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .style("font-size", "12px");

    // Add Y axis
    svg
      .append("g")
      .call(
        d3
          .axisLeft(y)
          .ticks(6)
          .tickFormat((d) => `$${(d as number) / 1000}k`),
      )
      .selectAll("text")
      .style("font-size", "12px");

    // Add bars with grow-from-baseline animation
    svg
      .selectAll(".bar")
      .data(monthlySpendData)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", (d) => x(d.month) || 0)
      .attr("y", height)
      .attr("width", x.bandwidth())
      .attr("height", 0)
      .attr("fill", colors.kleenway.green)
      .style("opacity", 0.9)
      .on("mouseover", function (this: SVGRectElement, event, d) {
        d3.select(this).style("opacity", 1).style("cursor", "pointer");
        // Show tooltip
        const tooltip = svg
          .append("g")
          .attr("class", "tooltip")
          .attr("transform", `translate(${x(d.month)! + x.bandwidth() / 2},${y(d.amount) - 10})`);

        tooltip
          .append("rect")
          .attr("x", -40)
          .attr("y", -25)
          .attr("width", 80)
          .attr("height", 20)
          .attr("fill", "rgba(0,0,0,0.8)")
          .attr("rx", 4);

        tooltip
          .append("text")
          .attr("text-anchor", "middle")
          .attr("y", -10)
          .attr("fill", "white")
          .attr("font-size", "12px")
          .text(`$${d.amount.toLocaleString()}`);
      })
      .on("mouseout", function (this: SVGRectElement) {
        d3.select(this).style("opacity", 0.9);
        svg.selectAll(".tooltip").remove();
      })
      .transition()
      .duration(700)
      .delay((_, i) => i * 40)
      .ease(d3.easeQuadOut)
      .attr("y", (d) => y(d.amount))
      .attr("height", (d) => height - y(d.amount));

    // Add Y axis label
    svg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin.left)
      .attr("x", 0 - height / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .style("font-size", "12px")
      .style("fill", colors.grey.dark)
      .text("Spend ($)");
  }, [monthlySpendData]);

  return (
    <WidgetContainer>
      <Stack gap={3}>
        <HeaderRow>
          <h1 style={{ fontWeight: 600, margin: 0 }}>{caption}</h1>
          <StaticDataBadge>Static Data</StaticDataBadge>
        </HeaderRow>
        <ChartContainer>
          <svg ref={svgRef}></svg>
        </ChartContainer>
      </Stack>
    </WidgetContainer>
  );
};

export default SpendPerMonthChart;

const WidgetContainer = styled.div`
  display: flex;
  border: 1px solid ${colors.grey.light11};
  border-top: 4px solid ${colors.kleenway.green};
  padding: 16px;
  border-radius: 6px;
`;

const ChartContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const StaticDataBadge = styled.span`
  background-color: ${colors.grey.light11};
  color: ${colors.grey.dark};
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;
